import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';

import { MenuSelect } from 'components/Select/menuSelect';

import EditableBlock from '../components/EditableBlock';

import { DEVICE_FRAME_WIDTH, BACKGROUND_IMAGE_EDITOR_FIELDS } from '../const';

import { ReactComponent as BgPadIcon } from 'assets/images/splashPage/bgpad.svg';
import { ReactComponent as BgPhoneIcon } from 'assets/images/splashPage/bgPhone.svg';

const DEVICE_BG_ICON = {
  mobile: BgPhoneIcon,
  tablet: BgPadIcon,
};

const PreviewFrame = ({ devicePreview, languageOptions, setLanguageOptions, selectedLanguage, editable, imageInfo, onUpdateImageBlock, children }) => {
  const theme = useTheme();

  const frameWidth = DEVICE_FRAME_WIDTH[devicePreview];
  const isMobile = devicePreview === 'mobile';
  const isDeviceFrame = isMobile || devicePreview === 'tablet';
  const DeviceBgIcon = DEVICE_BG_ICON[devicePreview];

  const getScreentInset = () => {
    if (devicePreview === 'mobile') {
      if (editable) {
        return { top: '8%', left: '6.7%', width: '86.2%', height: '84%' };
      } else {
        return { top: '8%', left: '6.95%', width: '85.8%', height: '84%' };
      }
    } else if (devicePreview === 'tablet') {
      if (editable) {
        return { top: '9.5%', left: '7.2%', width: '85.6%', height: '79.6%' };
      } else {
        return { top: '9.4%', left: '7.3%', width: '85.2%', height: '79.3%' };
      }
    }
  };

  const screenInset = getScreentInset();

  const handleLanguageChange = opt => {
    try {
      if (typeof setLanguageOptions === 'function') {
        setLanguageOptions(opt.value);
      }
    } catch (e) {
      console.error('Failed to set preview language', e);
    }
  };

  const renderLanguageSelector = padding => (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '6px',
        padding,
      }}
    >
      <MenuSelect
        width="140px"
        value={selectedLanguage}
        options={languageOptions}
        onChange={handleLanguageChange}
        startIcon={<LanguageOutlinedIcon sx={{ fontSize: 18, color: theme.palette.primary.greySecound }} />}
      />
    </Box>
  );

  const renderFooter = padding => (
    <Typography
      sx={{
        width: '100%',
        textAlign: 'center',
        fontSize: theme.typography.customFontSize[0],
        color: theme.palette.primary.greySecound,
        flexShrink: 0,
        padding,
      }}
    >
      Powered by D-Link Nuclias Unity
    </Typography>
  );

  // Tablet or Mobile
  if (isDeviceFrame) {
    return (
      <Box id="preview-frame" sx={{ width: frameWidth }}>
        <Box sx={{ position: 'relative', width: '100%', lineHeight: 0 }}>
          <DeviceBgIcon style={{ width: '100%', height: 'auto', display: 'block' }} />
          <Box
            sx={{
              position: 'absolute',
              top: screenInset.top,
              left: screenInset.left,
              width: screenInset.width,
              height: screenInset.height,
              overflowY: 'auto',
              lineHeight: 'normal',
            }}
          >
            <EditableBlock
              editable={editable}
              editorTitle="Background"
              editorFields={BACKGROUND_IMAGE_EDITOR_FIELDS}
              style={imageInfo?.background?.style}
              onStyleChange={style => onUpdateImageBlock('background', { style })}
              onClearIconClick={() => onUpdateImageBlock('background', { style: {}, content: '' })}
              showClearIcon={!!imageInfo?.background?.style?.content}
              blockSx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center' }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  overflow: 'auto',
                }}
              >
                {/* outer light-gray background container (one layer out) */}
                <Box
                  sx={{
                    width: '100%',
                    minHeight: '100%',
                    flexShrink: 0,
                    backgroundImage: imageInfo?.background?.style?.content ? `url(${imageInfo?.background?.style?.content})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: imageInfo?.background?.style?.backgroundColor || theme.palette.primary.white,
                    borderRadius: 0,
                    border: editable ? `1px dashed ${theme.palette.primary.greyThird}` : ``,
                    padding: isMobile ? '12px' : '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    boxSizing: 'border-box',
                  }}
                >
                  {renderLanguageSelector('0 0 8px')}

                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>{children}</Box>

                  {renderFooter(isMobile ? '6px 0 0' : '12px 0 0')}
                </Box>
              </Box>
            </EditableBlock>
          </Box>
        </Box>
      </Box>
    );
  } else {
    // Desktop
    return (
      <EditableBlock
        editable={editable}
        editorTitle="Background"
        editorFields={BACKGROUND_IMAGE_EDITOR_FIELDS}
        style={imageInfo?.background?.style}
        onStyleChange={style => onUpdateImageBlock('background', { style })}
        onClearIconClick={() => onUpdateImageBlock('background', { style: {}, content: '' })}
        showClearIcon={!!imageInfo?.background?.style?.content}
        blockSx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
      >
        <Box
          sx={{
            width: '100%',
            backgroundImage: imageInfo?.background?.style?.content ? `url(${imageInfo?.background?.style?.content})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: imageInfo?.background?.style?.backgroundColor || theme.palette.primary.white,
            borderRadius: theme.shape.customBorderRadius[1],
            border: editable ? `1px dashed ${theme.palette.primary.greyThird}` : `1px solid ${theme.palette.primary.borderGreyPrimary}`,
            boxShadow: editable ? 'none' : '0px 4px 16px rgba(0, 0, 0, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'visible',
          }}
        >
          {renderLanguageSelector('16px 16px 0')}

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '32px 48px 40px',
            }}
          >
            {children}
          </Box>

          {renderFooter('0 0 16px')}
        </Box>
      </EditableBlock>
    );
  }
};

export default PreviewFrame;
